import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../services/firebase';
import { expenseBaseCurrency } from '../utils/expenseUtils';
import type { TripConfig } from '../types';
import { branchList, type ItineraryBranch } from '../utils/itineraryBranches';
import type { BranchWorkspaceData } from '../components/ItineraryBranchWorkspace';

export function branchStorageId(tripId:string, branchId:string) {
  if (![tripId,branchId].every(id=>/^[\w-]{1,128}$/.test(id))) throw new Error('Invalid branch path.');
  return `${tripId}/branches/${branchId}`;
}
type Data = {key:string; summaries:ItineraryBranch[]; members:ItineraryBranch[]; configs:Record<string,TripConfig>; error:string; loading:boolean};

/** Private data is never rendered from disk cache: each session/reconnect waits
 * for server-authorised membership and all three booking/ledger subscriptions. */
export function useTripBranches(trip:TripConfig|null, userId:string|undefined,
  saveRoot:(next:TripConfig)=>Promise<boolean>):BranchWorkspaceData|undefined {
  const tripId=trip?.id;
  const key=`${tripId}:${userId}`;
  const [data,setData]=useState<Data>({key:'',summaries:[],members:[],configs:{},error:'',loading:true});
  const [selection,setSelection]=useState<{key:string;id:string|null}>({key:'',id:null});
  const [epoch,setEpoch]=useState(0);
  useEffect(()=>{
    const reconnect=()=>setEpoch(n=>n+1);
    const resume=()=>{if(document.visibilityState==='visible')reconnect();};
    window.addEventListener('online',reconnect);
    document.addEventListener('visibilitychange',resume);
    return ()=>{window.removeEventListener('online',reconnect);document.removeEventListener('visibilitychange',resume);};
  },[]);
  useEffect(()=>{
    if(!tripId || !userId) return;
    let active=true, summariesReady=false, branchesReady=false;
    const children=new Map<string,{stop:()=>void;config:TripConfig;loaded:Set<string>}>();
    setData({key,summaries:[],members:[],configs:{},error:'',loading:true});
    const patch=(update:(old:Data)=>Data)=>{if(active)setData(old=>old.key===key?update(old):old);};
    const clear=()=>{children.forEach(row=>row.stop());children.clear();};
    const failed=()=>{clear();patch(old=>({...old,configs:{},loading:false,error:'Could not load private branches. Reconnect or reload to try again.'}));};
    const publish=()=>patch(old=>({...old,loading:!summariesReady || !branchesReady || [...children.values()].some(row=>row.loaded.size<3),configs:Object.fromEntries([...children].filter(([,row])=>row.loaded.size===3).map(([id,row])=>[id,row.config]))}));
    const summaries=onSnapshot(collection(db,'trips',tripId,'branchSummaries'),{includeMetadataChanges:true},snap=>{
      summariesReady=!snap.metadata.fromCache;
      patch(old=>({...old,summaries:snap.docs.map(d=>({...d.data(),id:d.id,title:'',destinations:[]}) as ItineraryBranch)}));
      publish();
    },failed);
    const branches=onSnapshot(query(collection(db,'trips',tripId,'branches'),where('memberIds','array-contains',userId)),{includeMetadataChanges:true},snap=>{
      if(snap.metadata.fromCache){branchesReady=false;clear();patch(old=>({...old,configs:{},loading:true}));return;}
      branchesReady=true;
      patch(old=>({...old,members:snap.docs.map(d=>({...d.data(),id:d.id}) as ItineraryBranch)}));
      const ids=new Set(snap.docs.map(d=>d.id));
      for(const [id,row] of children) if(!ids.has(id)){row.stop();children.delete(id);}
      for(const d of snap.docs){
        const prior=children.get(d.id);
        const config={...d.data(),id:branchStorageId(tripId,d.id),branchId:d.id,rootTripId:tripId,
          itinerary:prior?.config.itinerary ?? {},expenses:prior?.config.expenses ?? [],payments:prior?.config.payments ?? [],
          childJunctions:(prior?.config as {childJunctions?:unknown}|undefined)?.childJunctions ?? {},
          childSummaries:(prior?.config as {childSummaries?:unknown}|undefined)?.childSummaries ?? []} as unknown as TripConfig;
        if(prior){prior.config=config;continue;}
        const row={config,loaded:new Set<string>(),stop:()=>{}};
        children.set(d.id,row);
        const stops=['itinerary','expenses','payments'].map(name=>onSnapshot(collection(d.ref,name),{includeMetadataChanges:true},child=>{
          if(child.metadata.fromCache || children.get(d.id)!==row) return;
          const value=name==='itinerary'?Object.fromEntries(child.docs.map(doc=>[doc.id,doc.data()])):child.docs.map(doc=>({...doc.data(),id:doc.id}));
          row.config={...row.config,[name]:value};row.loaded.add(name);publish();
        },failed));
        // Nested branches' safe split/rejoin events (R8). Optional decoration:
        // never gates readiness, and a failed read just shows undated rails.
        stops.push(onSnapshot(collection(d.ref,'junctions'),child=>{
          if(children.get(d.id)!==row) return;
          row.config={...row.config,childJunctions:Object.fromEntries(child.docs.map(doc=>[doc.id,doc.data().junctions ?? []]))} as TripConfig;
          publish();
        },()=>{}));
        // Nested branches of this one (their summaries live here, not on the trip).
        stops.push(onSnapshot(collection(d.ref,'childSummaries'),child=>{
          if(children.get(d.id)!==row) return;
          row.config={...row.config,childSummaries:child.docs.map(doc=>({...doc.data(),id:doc.id,title:'',destinations:[]}))} as TripConfig;
          publish();
        },()=>{}));
        row.stop=()=>stops.forEach(stop=>stop());
      }
      patch(old=>({...old,error:''}));publish();
    },failed);
    const offline=()=>{branchesReady=false;clear();patch(old=>({...old,configs:{},loading:true,error:'Reconnect to view private branches.'}));};
    window.addEventListener('offline',offline);
    return ()=>{active=false;summaries();branches();clear();window.removeEventListener('offline',offline);};
  },[key,tripId,userId,epoch]);
  return useMemo(()=>{
    if(!tripId || !userId) return undefined;
    const live=data.key===key?data:{key,summaries:[],members:[],configs:{},error:'',loading:true};
    const call=httpsCallable(functions,'manageItineraryBranch');
    return {
      branches:branchList(live.summaries,live.members,live.configs),
      persistence:{configs:live.configs,error:live.error,loading:live.loading,saveRoot,
        selected:selection.key===key?selection.id:null,
        select:(id:string|null)=>setSelection({key,id}),
        save:async(branch:ItineraryBranch)=>{
          const {title,parentId,memberIds,splitVisitId,rejoinVisitId,rejoinDate}=branch;
          const destinations=branch.destinations.map(({travellerIds,...d})=>d);
          await call({tripId,branchId:branch.id,revision:branch.revision ?? 0,action:'save',baseCurrency:expenseBaseCurrency(trip),branch:{title,parentId,memberIds,splitVisitId,rejoinVisitId,rejoinDate,destinations}});
        },
        leave:async(branch:ItineraryBranch,successorId?:string)=>{await call({tripId,branchId:branch.id,revision:branch.revision ?? 0,action:'leave',...(successorId?{successorId}:{})});},
      },
    };
  },[trip,tripId,userId,key,data,saveRoot,selection]);
}
